(() => {
  const sections = new WeakMap();
  const visibilityObservers = new WeakMap();
  const mobileQuery = window.matchMedia('(max-width: 749px)');

  const toBoolean = (value) => value === 'true';
  const toNumber = (value, fallback) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  };

  const shouldEnable = (section) => {
    const isMobile = mobileQuery.matches;
    return isMobile ? toBoolean(section.dataset.enableMobile) : toBoolean(section.dataset.enableSlider);
  };

  const updateSwiper = (swiper) => {
    if (!swiper || swiper.destroyed) return;

    swiper.updateSize();
    swiper.updateSlides();
    swiper.update();
  };

  const updateWhenVisible = (section, swiper) => {
    visibilityObservers.get(section)?.disconnect();

    const observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;

      window.requestAnimationFrame(() => updateSwiper(swiper));
      observer.disconnect();
      visibilityObservers.delete(section);
    });

    visibilityObservers.set(section, observer);
    observer.observe(section);
  };

  const destroySection = (section) => {
    if (!section) return;

    const swiper = sections.get(section);
    const visibilityObserver = visibilityObservers.get(section);

    if (visibilityObserver) {
      visibilityObserver.disconnect();
      visibilityObservers.delete(section);
    }

    if (swiper) {
      swiper.destroy(true, true);
      sections.delete(section);
    }

    section.classList.remove('is-swiper-active');
  };

  const initSection = (section) => {
    if (!section) return;

    const existingSwiper = sections.get(section);
    if (existingSwiper) {
      updateSwiper(existingSwiper);
      return;
    }

    if (!shouldEnable(section)) {
      destroySection(section);
      return;
    }

    const swiperElement = section.querySelector('[data-carson-testimonials-swiper]');
    const slides = swiperElement?.querySelectorAll('.swiper-slide') || [];

    if (!swiperElement || slides.length <= 1) return;

    const swiperReady = window.CarsonSwiperLoader?.readyFor(section);
    if (!swiperReady) return;

    swiperReady.then(() => {
      if (!shouldEnable(section) || sections.has(section)) return;

      const delay = toNumber(section.dataset.delay, 3000);
      const speed = toNumber(section.dataset.speed, 600);
      const gapDesktop = toNumber(section.dataset.gapDesktop, 28);
      const gapMobile = toNumber(section.dataset.gapMobile, 16);
      const prevButton = section.querySelector('[data-carson-testimonials-prev]');
      const nextButton = section.querySelector('[data-carson-testimonials-next]');
      const pagination = section.querySelector('[data-carson-testimonials-pagination]');

      const config = {
        slidesPerView: 'auto',
        spaceBetween: gapMobile,
        speed,
        a11y: {
          slideRole: 'listitem',
        },
        centeredSlides: false,
        observer: true,
        observeParents: true,
        resizeObserver: true,
        roundLengths: true,
        watchOverflow: true,
        loop: toBoolean(section.dataset.loop) && slides.length > 1,
        breakpoints: {
          750: {
            slidesPerView: 'auto',
            spaceBetween: gapDesktop,
          },
          990: {
            slidesPerView: 'auto',
            spaceBetween: gapDesktop,
          },
        },
      };

      if (toBoolean(section.dataset.autoplay)) {
        config.autoplay = {
          delay,
          disableOnInteraction: false,
          pauseOnMouseEnter: true,
        };
      }

      if (prevButton && nextButton) {
        config.navigation = {
          prevEl: prevButton,
          nextEl: nextButton,
        };
      }

      if (pagination) {
        config.pagination = {
          el: pagination,
          clickable: true,
        };
      }

      const swiper = new window.Swiper(swiperElement, config);
      sections.set(section, swiper);
      section.classList.add('is-swiper-active');
      updateWhenVisible(section, swiper);
    }).catch(() => {});
  };

  const refreshSection = (section) => {
    if (!section) return;

    if (shouldEnable(section)) {
      initSection(section);
      return;
    }

    destroySection(section);
  };

  const refreshAll = () => {
    document.querySelectorAll('.carson-testimonials').forEach(refreshSection);
  };

  document.addEventListener('DOMContentLoaded', refreshAll);
  window.addEventListener('load', refreshAll, { once: true });
  window.addEventListener('resize', refreshAll);

  document.addEventListener('shopify:section:load', (event) => {
    refreshSection(event.target.querySelector('.carson-testimonials'));
  });

  document.addEventListener('shopify:section:unload', (event) => {
    destroySection(event.target.querySelector('.carson-testimonials'));
  });
})();
